import { lory } from '@rsm/allfarblori';
import h from 'hyperscript';
import { generateIcon as icon } from 'components/_particles/icon/icon';
import { t } from 'javascripts/utils/withTranslation';
import { MEDIA_QUERIES } from 'javascripts/constants';

export default class BasicSlider {
  constructor($el, {
    autoplay = false,
    hasDots = false,
    hasCount = false,
    hasPagination = false,
    hasArrows = true,
    verticalImages = true,
    autoplayTime = 5000,
  }) {
    this.$el = $el;

    this.$slides = this.$el.querySelectorAll('.slider__slide');
    this.$slidesIndexes = [...this.$slides].map((slide, index) => index);
    this.totalSlides = this.$slides.length;
    this.$inner = this.$el.querySelector('.slider__inner');
    this.$slidesWrapper = this.$el.querySelector('.slider__slides-wrap');
    this.$slidesContainer = this.$el.querySelector('.slider__slides');

    // config
    this.minSlides = this.totalSlides > 1;
    this.hasDots = hasDots && this.minSlides;
    this.hasCount = hasCount && this.minSlides;
    this.hasPagination = hasPagination && this.minSlides;
    this.hasArrows = hasArrows && this.minSlides;
    this.autoplay = autoplay && this.minSlides;
    this.autoplayTime = autoplayTime;
    this.autoplayPlays = this.autoplay;
    this.verticalImages = verticalImages;
    this.activeIndex = 0;
    this.slideWidth = this.getActiveSlideWidth();

    this.initEvents();
    this.lory = lory(this.$el, {
      rewind: true,
      rewindPrev: true,
      rewindSpeed: 200,
      slideSpeed: 350,
      ease: 'cubic-bezier(0.455, 0.03, 0.515, 0.955)',
      classNameFrame: 'slider__slides-wrap',
      classNameSlideContainer: 'slider__slides',
      classNamePrevCtrl: 'slider__arrow--prev',
      classNameNextCtrl: 'slider__arrow--next',
      classNameDisabledPrevCtrl: 'slider__arrow--disabled',
      classNameDisabledNextCtrl: 'slider__arrow--disabled',
    });
  }

  deconstructor() {
    if (this.hasArrows) {
      this.removeArrows();
    }

    if (this.hasPagination) {
      this.removePagination();
    }

    this.removeControls();
    this.$el.classList.remove('slider--initialized');
    this.lory.destroy();
  }

  initEvents() {
    this.$el.addEventListener('before.lory.init', () => this.handleLoryInit());

    this.$el.addEventListener('after.lory.init', () => this.handleLoryAfterInit());

    this.$el.addEventListener('on.lory.resize', () => this.handleLoryResize());

    this.$el.addEventListener('after.lory.slide', () => this.handleLorySlide());

    window.addEventListener('selectTab', () => {
      this.handleLoryResize();
      this.lory.reset();
    });

    this.$slidesContainer.addEventListener('focusin', this.focusIn);
  }

  handleLoryInit() {
    this.$slidesWrapper.scrollLeft = 0;
    this.initSlideControls();

    if (this.hasArrows) {
      this.renderArrows();
    }

    if (this.hasPagination) {
      this.renderPagination();
    }

    if (this.hasDots) {
      this.renderDots();
      this.renderAutoplayButton();
    }

    if (this.hasCount) {
      this.renderSlideCount();
    }

    if (this.verticalImages) {
      this.sizeVerticalImages();
    }

    this.setContainerHeight();
    this.positionControls();
  }

  handleLoryAfterInit() {
    this.$el.classList.add('slider--initialized');

    if (this.autoplay) {
      this.autoplayStart();
    }
  }

  handleLorySlide() {
    if (this.hasDots) {
      this.updateDots();
    }

    if (this.hasCount) {
      this.updateSlideCount();
    }

    if (this.hasPagination) {
      this.updatePaginationPages();
    }

    if (this.hasArrows) {
      this.positionArrows();
    }

    if (this.verticalImages) {
      this.sizeVerticalImages();
    }

    this.setContainerHeight();
    this.positionControls();
  }

  handleLoryResize() {
    if (this.hasArrows) {
      this.positionArrows();
    }

    if (this.verticalImages) {
      this.sizeVerticalImages();
    }

    this.setContainerHeight();
    this.positionControls();
  }

  // add slide controls to DOM as a container for dots and count
  initSlideControls() {
    this.$slideControlsInner = h('.slider__slide-controls-inner');
    this.$slideControls = h('.slider__slide-controls', this.$slideControlsInner);
    this.$inner.appendChild(this.$slideControls);
  }

  // add navigation arrows to DOM
  renderArrows() {
    this.$controlPrev = h(
      'button.slider__arrow.slider__arrow--prev',
      {
        type: 'button',
        tabIndex: '-1',
        attrs: {
          'aria-label': t('Vorheriger Slide'),
        },
        onclick: () => this.autoplayStop(),
      },
      icon({
        icon: 'slider-arrow-left',
        viewBox: '0 0 70 200',
        className: 'slider__arrow-icon',
      }),
    );

    this.$controlNext = h(
      'button.slider__arrow.slider__arrow--next',
      {
        type: 'button',
        tabIndex: '-1',
        attrs: {
          'aria-label': t('Nächster Slide'),
        },
        onclick: () => this.autoplayStop(),
      },
      icon({
        icon: 'slider-arrow-right',
        viewBox: '0 0 70 200',
        className: 'slider__arrow-icon',
      }),
    );
    this.$inner.appendChild(this.$controlPrev);
    this.$inner.appendChild(this.$controlNext);
    this.positionArrows();
  }

  // add Dot Navigation, one dot for each slide
  renderDots = () => {
    const $dots = h('div.dots');

    this.$slides.forEach((_, index) =>
      $dots.appendChild(h(`button.dots__dot${index === 0 ? '.dots__dot--active' : ''}`, {
        type: 'button',
        tabIndex: '-1',
        onclick: () => {
          this.lory.slideTo(index);
          this.autoplayStop();
        },
      }, h('span.u-hidden-visually', `${t('Zu Slide')}: ${index}`))));

    const $inner = h('div.slider__dots-inner', $dots);
    this.$dots = h('div.slider__dots', $inner);
    this.$slideControlsInner.appendChild(this.$dots);
  }

  // Add autoplayContainer to DOM and render a default playbutton
  renderAutoplayButton = () => {
    const $playIcon = icon({ icon: 'play', title: t('Autoplay an'), className: 'slider__autoplay-icon' });
    const $autoPlayButton = h(
      'button.slider__autoplay-button', {
        type: 'button',
        tabIndex: '-1',
        onclick: this.toggleAutoPlayClick,
      },
      $playIcon,
    );

    this.$autoplayContainer = h('.slider__autoplay', $autoPlayButton);
    const $inner = this.$dots.querySelector('.slider__dots-inner');
    $inner.insertBefore(this.$autoplayContainer, $inner.firstChild);
  }

  // add pagination for galleries
  renderPagination() {
    const $prevButton = h(
      'button.button.button--secondary.button--reversed.pagination__icon-link',
      {
        type: 'button',
        onclick: () => this.lory.prev(),
      },
      icon({
        icon: 'arrow-left',
        className: 'button__icon icon--arrow-left',
      }),
      h('span.button__text', t('Zurück')),
    );

    const $pages = this.getPaginationPages();

    const $nextButton = h(
      'button.button.button--secondary.pagination__icon-link',
      {
        type: 'button',
        onclick: () => this.lory.next(),
      },
      icon({
        icon: 'arrow-right',
        className: 'button__icon icon--arrow-right',
      }),
      h('span.button__text', t('Weiter')),
    );

    this.$paginationWrapper = h(
      '.pagination.slider__pagination',
      {
        'aria-label': t('Seitennummerierung'),
      },
      $prevButton,
      $pages,
      $nextButton,
    );

    this.$inner.appendChild(this.$paginationWrapper);
  }

  // calculate which page numbers are shown in the pagination and generate the DOM nodes
  getPaginationPages() {
    const activeIndex = this.lory ? this.lory.returnIndex() : 0;

    const isDotVisible = (i) => i === 0 || i === this.$slides.length - 1 || activeIndex === i || activeIndex === i - 1; // eslint-disable-line

    const $pagination = h('ul.pagination__list', this.$slidesIndexes.map((index) => {
      if (!isDotVisible(index - 1) && !isDotVisible(index)) {
        return undefined;
      }

      if (isDotVisible(index - 1) && !isDotVisible(index)) {
        return h('li.pagination__item', '…');
      }

      const ariaCurrent = index === activeIndex ? { 'aria-current': 'page' } : undefined;

      const $paginationButton = h(
        `button.link.link--inverted.pagination__link${
          index === activeIndex ? '.pagination__link--current' : ''
        }`,
        {
          type: 'button',
          onclick: () => this.lory.slideTo(index),
          attrs: ariaCurrent,
        },
        h(
          'span.u-hidden-visually',
          index === this.$slides.length - 1 ? t('Zur letzten Seite') : t('Zur Seite'),
        ),
        index + 1,
      );

      return h(
        `li.pagination__item${index === activeIndex ? '.pagination__item--current' : ''}`,
        $paginationButton,
      );
    }));

    return $pagination;
  }

  // add initial count to slide controls
  renderSlideCount = () => {
    const $slideCount = h('span.slider__slide-count-inner', `1/${this.totalSlides}`);
    this.$slideCountContainer = h('.slider__slide-count');
    this.$slideCountContainer.appendChild($slideCount);
    this.$slideControlsInner.appendChild(this.$slideCountContainer);
  }

  // calculate the arrow positions depending on images
  positionArrows() {
    const $activeSlide =
      this.$el.querySelector('.slider__slide.active') || this.$el.querySelector('.slider__slide');

    if ($activeSlide) {
      const $activeSlideMedia = $activeSlide.querySelector('.figure__media');

      this.$controlPrev.style.top = `${$activeSlideMedia.offsetTop + ($activeSlideMedia.clientHeight / 2)}px`;
      this.$controlNext.style.top = `${$activeSlideMedia.offsetTop + ($activeSlideMedia.clientHeight / 2)}px`;
    }
  }

  // calculate the control positions depending on images
  positionControls() {
    const $activeSlide =
      this.$el.querySelector('.slider__slide.active') || this.$el.querySelector('.slider__slide');

    if ($activeSlide) {
      const $activeSlideMedia = $activeSlide.querySelector('.figure__media');

      this.$slideControls.style.top = `${$activeSlideMedia.offsetTop + $activeSlideMedia.clientHeight + 20}px`;
    }
  }

  // calculate slide container height
  setContainerHeight() {
    const $activeSlide =
      this.$el.querySelector('.slider__slide.active') || this.$el.querySelector('.slider__slide');

    if ($activeSlide) {
      const $activeSlideFirstChild = $activeSlide.firstElementChild;

      this.$slidesWrapper.style.height = `${$activeSlideFirstChild.clientHeight}px`;
    }
  }

  // Vertical images should not be 100% wide, but restricted to a specific height and centered
  sizeVerticalImages() {
    this.$slides.forEach(($slide) => {
      const $figure = $slide.querySelector('.figure');

      if ($figure) {
        const $imageWrap = $figure.querySelector('.image');
        const $img = $figure.querySelector('.image__img');
        const $copyright = $figure.querySelector('.figure__copyright');

        if ($img) {
          const paddingTop = parseInt(window.getComputedStyle($img).getPropertyValue('padding-top').split('%')[0], 10);
          const definedWidth = parseInt($img.getAttribute('width'), 10);
          const definedHeight = parseInt($img.getAttribute('height'), 10);
          const ratio = (definedHeight / definedWidth);

          const isHorizontal = definedWidth <= definedHeight || paddingTop > 100 || ratio > 1;

          if ($img && isHorizontal) {
            $imageWrap.removeAttribute('style');
            $imageWrap.classList.add('slider__image-vertical');

            if ($copyright && window.matchMedia(MEDIA_QUERIES.m).matches) {
              const copyrightWidth = $imageWrap.clientHeight / ratio;
              $imageWrap.appendChild($copyright);
              $copyright.setAttribute('style', `width: ${copyrightWidth}px; margin-top: -${$copyright.clientHeight + 1}px`);
            } else if ($copyright) {
              $copyright.removeAttribute('style');
              $imageWrap.parentNode.appendChild($copyright);
            }
          }
        }
      }
    });
    this.setContainerHeight();
    this.positionControls();
    this.positionArrows();
  }

  updateSlideCount() {
    document.querySelector('.slider__slide-count-inner').innerText = `${this.lory.returnIndex() + 1}/${this.totalSlides}`;
  }

  updatePaginationPages() {
    const $pages = this.getPaginationPages();
    if ($pages) {
      this.$paginationWrapper.replaceChild($pages, document.querySelector('.pagination__list'));
    }
  }

  updateDots() {
    const $dots = this.$dots.querySelectorAll('.dots__dot');

    $dots.forEach(($dot, index) => {
      $dot.classList.remove('dots__dot--active');
      if (this.lory.returnIndex() === index) {
        $dot.classList.add('dots__dot--active');
      }
    });
  }

  removeArrows() {
    if (this.$controlNext && this.$controlNext.parentNode) {
      this.$controlNext.parentNode.removeChild(this.$controlNext);
    }

    if (this.$controlPrev && this.$controlPrev.parentNode) {
      this.$controlPrev.parentNode.removeChild(this.$controlPrev);
    }
  }

  removePagination() {
    if (this.$paginationWrapper && this.$paginationWrapper.parentNode) {
      this.$paginationWrapper.parentNode.removeChild(this.$paginationWrapper);
    }
  }

  removeControls() {
    if (this.$slideControls && this.$slideControls.parentNode) {
      this.$slideControls.parentNode.removeChild(this.$slideControls);
    }
  }

  toggleAutoPlayClick = () => {
    if (this.autoplayPlays) {
      this.autoplayStop();
    } else {
      this.autoplayStart();
    }
  }

  autoplayStop = () => {
    if (this.$autoplayContainer) {
      while (this.$autoplayContainer.firstChild) {
        this.$autoplayContainer.removeChild(this.$autoplayContainer.firstChild);
      }

      const $playIcon = icon({ icon: 'play', className: 'slider__autoplay-icon' });

      const $autoPlayButtonPlay = h(
        'button.slider__autoplay-button',
        {
          type: 'button',
          title: t('Autoplay an'),
          onclick: this.toggleAutoPlayClick,
        }, $playIcon,
      );

      this.$autoplayContainer.appendChild($autoPlayButtonPlay);
    }
    clearInterval(this.autoplayInvervall);
    this.autoplayPlays = false;
  }

  autoplayStart = () => {
    if (this.$autoplayContainer) {
      while (this.$autoplayContainer.firstChild) {
        this.$autoplayContainer.removeChild(this.$autoplayContainer.firstChild);
      }

      const $pauseIcon = icon({ icon: 'pause', className: 'slider__autoplay-icon' });

      const $autoPlayButtonPause = h(
        'button.slider__autoplay-button',
        {
          type: 'button',
          title: t('Autoplay aus'),
          onclick: this.toggleAutoPlayClick,
        },
        $pauseIcon,
      );

      this.autoplayInvervall = setInterval(() => {
        this.lory.next();
      }, this.autoplayTime);
      this.$autoplayContainer.appendChild($autoPlayButtonPause);
      this.autoplayPlays = true;
    }
  }

  updateTeaserPosition = (index) => {
    this.lory.slideTo(index);
  };

  getActiveSlideWidth = () => this.$slides[this.activeIndex].offsetWidth;

  focusIn = (event) => {
    if (event.target && !this.mousedown) {
      this.$slidesWrapper.scrollLeft = 0;
      const $slide = event.target.closest('.slider__slide');
      if ($slide) {
        const index = Array.prototype.indexOf.call(this.$slides, $slide);
        this.activeIndex = index;
        this.updateTeaserPosition(index);
      }
    }
  }
}
